import React from 'react';
import { Link } from 'react-router-dom';
import {
  PenLine,
  Eye,
  Ear,
  Repeat,
  Play,
  ArrowRight,
  BookOpen,
} from 'lucide-react';
import SEO from '../components/SEO';
import Header from '../components/Header';
import Footer from '../components/Footer';

const redGradient =
  'linear-gradient(135deg,rgba(255,255,255,.15),transparent 50%,rgba(0,0,0,.15)), #b30000';

const Eyebrow: React.FC<{ children: React.ReactNode; light?: boolean }> = ({ children, light }) => (
  <p className={`text-xs font-semibold tracking-[0.12em] uppercase mb-3 ${light ? 'text-white/60' : 'text-[#970000]'}`}>
    {children}
  </p>
);

// Alle 28 Buchstaben des arabischen Alphabets in alphabetischer Reihenfolge
const letters = [
  { ar: 'ا', name: 'Alif', tr: 'ā' },
  { ar: 'ب', name: 'Bā', tr: 'b' },
  { ar: 'ت', name: 'Tā', tr: 't' },
  { ar: 'ث', name: 'Thā', tr: 'th' },
  { ar: 'ج', name: 'Dschīm', tr: 'dsch' },
  { ar: 'ح', name: 'Hā', tr: 'ḥ' },
  { ar: 'خ', name: 'Chā', tr: 'ch' },
  { ar: 'د', name: 'Dāl', tr: 'd' },
  { ar: 'ذ', name: 'Dhāl', tr: 'dh' },
  { ar: 'ر', name: 'Rā', tr: 'r' },
  { ar: 'ز', name: 'Zāy', tr: 'z' },
  { ar: 'س', name: 'Sīn', tr: 's' },
  { ar: 'ش', name: 'Schīn', tr: 'sch' },
  { ar: 'ص', name: 'Sād', tr: 'ṣ' },
  { ar: 'ض', name: 'Dād', tr: 'ḍ' },
  { ar: 'ط', name: 'Tā', tr: 'ṭ' },
  { ar: 'ظ', name: 'Zā', tr: 'ẓ' },
  { ar: 'ع', name: 'ʿAyn', tr: 'ʿ' },
  { ar: 'غ', name: 'Ghayn', tr: 'gh' },
  { ar: 'ف', name: 'Fā', tr: 'f' },
  { ar: 'ق', name: 'Qāf', tr: 'q' },
  { ar: 'ك', name: 'Kāf', tr: 'k' },
  { ar: 'ل', name: 'Lām', tr: 'l' },
  { ar: 'م', name: 'Mīm', tr: 'm' },
  { ar: 'ن', name: 'Nūn', tr: 'n' },
  { ar: 'ه', name: 'Hā', tr: 'h' },
  { ar: 'و', name: 'Wāw', tr: 'w/ū' },
  { ar: 'ي', name: 'Yā', tr: 'y/ī' },
];

const alphabetFaqs = [
  {
    question: 'Wie lange dauert es, das arabische Alphabet zu lernen?',
    answer:
      'Mit strukturiertem Unterricht und regelmäßigem Üben lernen die meisten Anfänger das arabische Alphabet in 3 bis 5 Monaten sicher lesen und schreiben. In unserem Buchstabenkurs erreichst du das in etwa 20 Wochen – inklusive korrekter Aussprache und grundlegender Leseregeln.',
  },
  {
    question: 'Ist das arabische Alphabet schwer zu lernen?',
    answer:
      'Nein – es wirkt nur ungewohnt. Das arabische Alphabet hat 28 Buchstaben und ist ein reines Lautsystem: Was du liest, sprichst du auch. Die größte Umstellung ist die Schreibrichtung von rechts nach links und dass Buchstaben je nach Position im Wort ihre Form leicht verändern. Mit der richtigen Reihenfolge und Anleitung ist das in wenigen Wochen vertraut.',
  },
  {
    question: 'Kann ich Arabisch lernen, ohne die Schrift zu lernen?',
    answer:
      'Davon raten wir ab. Lautschrift-Umschriften sind ungenau und führen zu Aussprachefehlern, die man später schwer korrigiert. Wer den Quran, arabische Bücher oder Medien verstehen will, kommt an der Originalschrift nicht vorbei. Das Alphabet ist der schnellste und stabilste Einstieg – deshalb beginnt unser Kursweg genau dort.',
  },
  {
    question: 'Wie viele Buchstaben hat das arabische Alphabet?',
    answer:
      'Das arabische Alphabet hat 28 Buchstaben. Alle sind Konsonanten oder Langvokale; kurze Vokale werden durch Hilfszeichen (Harakat) über oder unter den Buchstaben angezeigt. Dazu kommen wenige Sonderzeichen wie Hamza und Tā marbūta, die du im Kurs mitlernst.',
  },
  {
    question: 'In welche Richtung schreibt man Arabisch?',
    answer:
      'Arabisch wird von rechts nach links geschrieben und gelesen. Zahlen werden dagegen von links nach rechts geschrieben. Nach den ersten Übungswochen fühlt sich die Schreibrichtung völlig natürlich an.',
  },
];

const ArabischAlphabetPage = () => {
  const specials = [
    { icon: PenLine, h: 'Von rechts nach links', t: 'Arabisch wird von rechts nach links geschrieben und gelesen. Das klingt ungewohnt, wird aber nach wenigen Übungsstunden zur Selbstverständlichkeit.' },
    { icon: Eye, h: 'Buchstaben verbinden sich', t: 'Die meisten Buchstaben verändern ihre Form leicht, je nachdem ob sie am Anfang, in der Mitte oder am Ende eines Wortes stehen – wie eine elegante Schreibschrift.' },
    { icon: Ear, h: 'Ein reines Lautsystem', t: 'Anders als im Englischen gilt: Was geschrieben steht, wird auch so gesprochen. Wer die Buchstaben kennt, kann jedes Wort korrekt lesen.' },
    { icon: Repeat, h: 'Kurze Vokale als Zeichen', t: 'Kurze Vokale (a, i, u) werden durch kleine Hilfszeichen – die Harakat – über und unter den Buchstaben markiert. Im Quran sind sie immer gesetzt.' },
  ];

  const steps = [
    { h: 'Buchstaben erkennen', t: 'Du lernst jeden der 28 Buchstaben einzeln kennen – Form, Name und Laut. Ähnlich aussehende Buchstaben werden bewusst gegenübergestellt, damit du sie sicher unterscheidest.' },
    { h: 'Schreiben üben', t: 'Jeder Buchstabe wird in allen Positionen (Anfang, Mitte, Ende) von Hand geschrieben. Das Schreiben verankert die Formen im Gedächtnis – deshalb gehört es von Anfang an dazu.' },
    { h: 'Laute korrekt aussprechen', t: 'Arabisch hat Laute, die es im Deutschen nicht gibt (z. B. ع und ح). Im Live-Unterricht hörst und übst du sie mit direktem Feedback – das kann keine App ersetzen.' },
    { h: 'Wörter und Sätze lesen', t: 'Sobald die Buchstaben sitzen, liest du erste Wörter, dann kurze Sätze – inklusive grundlegender Tajweed-Regeln für die Quran-Rezitation.' },
  ];

  return (
    <>
      <SEO
        title="Arabisches Alphabet lernen: Alle 28 Buchstaben einfach erklärt"
        description="Arabisches Alphabet lernen leicht gemacht: alle 28 Buchstaben mit Aussprache, die wichtigsten Regeln und der schnellste Weg zum flüssigen Lesen – mit Live-Unterricht auf Deutsch."
        keywords="Arabisches Alphabet lernen, arabische Buchstaben lernen, Arabisch lesen lernen, Arabisch schreiben lernen, arabisches Alphabet Aussprache, arabische Schrift lernen, Buchstabenkurs Arabisch, Arabisch Alphabet für Anfänger"
        url="/arabisch-alphabet-lernen"
        faqItems={alphabetFaqs}
      />
      <Header />

      {/* Hero */}
      <section className="relative bg-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0">
          <div
            className="absolute inset-0 opacity-50"
            style={{
              backgroundImage:
                'linear-gradient(to right,rgba(26,6,6,.06) 1px,transparent 1px),linear-gradient(to bottom,rgba(26,6,6,.06) 1px,transparent 1px)',
              backgroundSize: '48px 48px',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-white via-white/50 to-white" />
        </div>
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="max-w-3xl">
            <Eyebrow>Arabisch lesen &amp; schreiben</Eyebrow>
            <h1 className="text-4xl sm:text-5xl md:text-6xl font-extrabold tracking-[-0.04em] leading-[1.05] text-[#1a0606] mb-6">
              Das arabische Alphabet<br />
              <em className="not-italic oblique text-[#970000]">lernen</em> – Schritt für Schritt.
            </h1>
            <p className="text-[16px] md:text-[18px] leading-relaxed text-gray-500 mb-8 max-w-2xl">
              Alle 28 Buchstaben, ihre Aussprache und die wichtigsten Regeln – verständlich erklärt.
              Plus: der bewährte Weg, in 3–5 Monaten flüssig Arabisch lesen und schreiben zu lernen.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/anmelden"
                className="inline-flex items-center gap-2 rounded-full px-6 py-3.5 font-bold text-[14.5px] text-white shadow-[0_8px_20px_-8px_rgba(151,0,0,0.35)] transition-all duration-150 hover:brightness-110 hover:-translate-y-px"
                style={{ background: redGradient }}
              >
                ▶ Buchstabenkurs starten
              </Link>
              <a
                href="#alle-buchstaben"
                className="inline-flex items-center gap-2 rounded-full border-[1.5px] border-[#1a0606]/[0.18] bg-white/70 px-6 py-3.5 font-bold text-[14.5px] text-[#1a0606] transition-all duration-150 hover:border-[#970000] hover:text-[#970000]"
              >
                Alle Buchstaben ansehen →
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Besonderheiten der arabischen Schrift */}
      <section id="arabische-schrift-besonderheiten" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Die Grundlagen</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Was macht die <span className="oblique font-extrabold text-[#970000]">arabische Schrift</span> besonders?
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Das arabische Alphabet ist logischer aufgebaut, als viele denken. Vier Eigenschaften solltest
              du kennen, bevor du mit dem Lernen beginnst:
            </p>
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            {specials.map(({ icon: Icon, h, t }) => (
              <div key={h} className="rounded-2xl border border-gray-100 bg-white p-5 hover:border-[#970000]/30 transition-all duration-200">
                <div className="w-9 h-9 rounded-xl bg-[#970000]/[0.08] flex items-center justify-center text-[#970000] mb-4">
                  <Icon className="h-4 w-4" />
                </div>
                <h3 className="text-[15px] font-semibold text-gray-900 mb-1.5">{h}</h3>
                <p className="text-[13px] text-gray-500 leading-relaxed">{t}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Alle 28 Buchstaben */}
      <section id="alle-buchstaben" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Die Übersicht</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Alle <span className="oblique font-extrabold text-[#970000]">28 arabischen Buchstaben</span> im Überblick
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Das vollständige arabische Alphabet in alphabetischer Reihenfolge – mit Name und
              Umschrift. Im Buchstabenkurs lernst du zu jedem Buchstaben zusätzlich die
              Formen am Wortanfang, in der Mitte und am Ende.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3">
            {letters.map(({ ar, name, tr }) => (
              <div key={`${ar}-${name}`} className="rounded-2xl border border-gray-100 bg-white p-4 text-center hover:border-[#970000]/30 transition-all duration-200">
                <p className="font-arabic text-4xl leading-none text-[#1a0606] mb-3" lang="ar" dir="rtl">{ar}</p>
                <p className="text-[13px] font-semibold text-gray-900">{name}</p>
                <p className="text-[12px] text-gray-400">{tr}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* So lernst du das Alphabet */}
      <section id="alphabet-lernen-schritte" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Die Methode</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              So lernst du das arabische Alphabet – <span className="oblique font-extrabold text-[#970000]">in 4 Schritten</span>
            </h2>
            <p className="mt-4 text-[15px] md:text-[17px] leading-relaxed text-gray-500">
              Der häufigste Fehler: Buchstaben nur passiv anschauen. Lesen lernt man durch
              Erkennen, Schreiben, Hören und Sprechen – in dieser Reihenfolge arbeiten wir im Kurs:
            </p>
          </div>
          <ol className="grid md:grid-cols-2 gap-4 list-none">
            {steps.map(({ h, t }, i) => (
              <li key={h} className="rounded-2xl border border-gray-100 bg-white p-5 hover:border-[#970000]/30 transition-all duration-200">
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-7 h-7 rounded-full bg-[#970000] text-white text-[13px] font-bold flex items-center justify-center shrink-0">
                    {i + 1}
                  </span>
                  <h3 className="text-[15px] font-semibold text-gray-900">{h}</h3>
                </div>
                <p className="text-[13px] text-gray-500 leading-relaxed">{t}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Buchstabenkurs — Split mit roter Karte */}
      <section id="buchstabenkurs" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-6 md:gap-8 items-start">
            <div>
              <Eyebrow>Der Kurs dazu</Eyebrow>
              <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.15] text-gray-900 mb-5">
                Der Buchstabenkurs: <span className="oblique font-extrabold text-[#970000]">Arabisch lesen lernen</span> mit Live-Unterricht
              </h2>
              <div className="space-y-4 text-[15px] leading-[1.8] text-gray-500">
                <p>
                  Unser <strong className="font-semibold text-gray-700">Buchstabenkurs (Level 1)</strong> bringt
                  dich in etwa 20 Wochen vom ersten Buchstaben zum flüssigen Lesen – live, auf Deutsch,
                  in kleinen Gruppen. Du übst Aussprache mit direktem Feedback vom Lehrer und lernst
                  nebenbei grundlegende Tajweed-Regeln für die Quran-Rezitation.
                </p>
                <p>
                  Danach geht es nahtlos weiter: Grammatik und Wortschatz im Grundlagenkurs, bis hin zum
                  selbstständigen Lesen arabischer Originaltexte. Den kompletten Lernweg findest du in
                  unserem <Link to="/arabisch-lernen" className="font-semibold text-[#970000] hover:underline">Leitfaden zum Arabisch lernen</Link> –
                  und wer vor allem den Quran verstehen möchte, liest hier weiter: <Link to="/quran-arabisch-lernen" className="font-semibold text-[#970000] hover:underline">Quran-Arabisch lernen</Link>.
                </p>
              </div>
            </div>
            <div className="relative overflow-hidden rounded-2xl bg-[#b30000] p-6 md:p-8 text-white">
              <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/15" />
              <div className="relative">
                <Eyebrow light>Level 1</Eyebrow>
                <p className="text-[17px] md:text-[19px] font-bold tracking-[-0.02em] leading-snug text-white mb-4">
                  In ca. 20 Wochen das arabische Alphabet sicher lesen, schreiben und aussprechen.
                </p>
                <ul className="space-y-2 text-[13px] text-white/80 mb-6">
                  <li className="flex items-start gap-2"><BookOpen className="h-4 w-4 mt-0.5 shrink-0" /> Alle 28 Buchstaben in allen Formen</li>
                  <li className="flex items-start gap-2"><Ear className="h-4 w-4 mt-0.5 shrink-0" /> Aussprache mit Live-Feedback</li>
                  <li className="flex items-start gap-2"><Repeat className="h-4 w-4 mt-0.5 shrink-0" /> Grundlegende Tajweed-Regeln</li>
                </ul>
                <Link
                  to="/anmelden"
                  className="inline-flex items-center gap-2 rounded-full bg-white px-5 py-2.5 text-[13px] font-semibold text-[#970000] transition-colors duration-200 hover:bg-[#f5f0ef]"
                >
                  <Play className="h-3.5 w-3.5 fill-[#970000]" />
                  Jetzt kostenlos anmelden
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section id="alphabet-faq" className="bg-white py-16 md:py-24">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mb-10 md:mb-12">
            <Eyebrow>Häufige Fragen</Eyebrow>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-[-0.04em] leading-[1.1] text-gray-900">
              Häufige Fragen zum <span className="oblique font-extrabold text-[#970000]">arabischen Alphabet</span>
            </h2>
          </div>
          <div className="grid gap-4 max-w-3xl">
            {alphabetFaqs.map(({ question, answer }) => (
              <div key={question} className="rounded-2xl border border-gray-100 bg-white p-5">
                <h3 className="text-[15px] font-semibold text-gray-900 mb-2">{question}</h3>
                <p className="text-[13.5px] text-gray-500 leading-relaxed">{answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-16 md:py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative overflow-hidden rounded-[22px] bg-[#b30000] px-8 py-10 md:px-12 md:py-12">
            <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-white/15 via-transparent to-black/15" />
            <div className="relative flex flex-col lg:flex-row lg:items-center lg:justify-between gap-8">
              <div className="max-w-xl">
                <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold leading-[1.05] tracking-[-0.03em] text-white">
                  Starte mit dem <em className="not-italic oblique">ersten Buchstaben</em>.
                </h2>
                <p className="mt-4 text-[15px] text-white/70 font-normal">
                  Melde dich jetzt für den Buchstabenkurs an – kostenlos und unverbindlich.
                </p>
              </div>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 shrink-0">
                <Link
                  to="/anmelden"
                  className="inline-flex items-center justify-center gap-2 rounded-full bg-white px-6 py-3 text-[15px] font-semibold text-[#970000] transition-colors duration-200 hover:bg-[#f5f0ef]"
                >
                  <Play className="h-3.5 w-3.5 fill-[#970000]" />
                  Jetzt kostenlos anmelden
                </Link>
                <Link
                  to="/arabisch-lernen"
                  className="inline-flex items-center justify-center gap-2 rounded-full border border-white/30 px-6 py-3 text-[15px] font-semibold text-white transition-colors duration-200 hover:bg-white/10"
                >
                  Zum Arabisch-Leitfaden
                  <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default ArabischAlphabetPage;
